/**
 * DeepLinkPicker (FEAT-DEEPLINK-001) — a drop-in "From library" button + modal
 * that lets any screen pull values from the central Deep Link Library.
 *
 * It owns NO host state: on selection it calls `onPick(entry)` with the full
 * library row ({ name, android_link, ios_link, web_fallback_url, category, ... })
 * and the host copies whichever field(s) it needs. The copied value is a plain
 * string — editing the library later never changes an already-copied field.
 *
 * Usage:
 *   <DeepLinkPicker onPick={(e) => updateField('deep_link', e.android_link || e.ios_link)} />
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Button, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody,
  ModalCloseButton, Input, InputGroup, InputLeftElement, Box, Flex, Text, Badge,
  Spinner, Center, VStack, useDisclosure, Tooltip,
} from '@chakra-ui/react';
import { FiLink, FiSearch, FiInbox } from 'react-icons/fi';
import deepLinksApi from '../../api/deepLinks';

function LinkRow({ entry, onChoose }) {
  return (
    <Box
      as="button" type="button" textAlign="left" w="100%" px={3} py={2.5}
      borderWidth="1px" borderColor="#ebedf0" borderRadius="10px"
      _hover={{ borderColor: 'brand.400', bg: 'brand.50' }} transition="all .12s"
      onClick={() => onChoose(entry)}
    >
      <Flex justify="space-between" align="center" gap={2}>
        <Text fontWeight={600} fontSize="sm" noOfLines={1}>{entry.name}</Text>
        {entry.category ? <Badge colorScheme="gray" fontSize="10px">{entry.category}</Badge> : null}
      </Flex>
      <VStack align="stretch" spacing={0.5} mt={1.5}>
        {entry.android_link ? <Text fontSize="11px" color="gray.500" fontFamily="mono" noOfLines={1}>Android · {entry.android_link}</Text> : null}
        {entry.ios_link ? <Text fontSize="11px" color="gray.500" fontFamily="mono" noOfLines={1}>iOS · {entry.ios_link}</Text> : null}
        {entry.web_fallback_url ? <Text fontSize="11px" color="gray.400" fontFamily="mono" noOfLines={1}>Web · {entry.web_fallback_url}</Text> : null}
      </VStack>
    </Box>
  );
}

export default function DeepLinkPicker({ onPick, label = 'From library', iconOnly = false, buttonProps = {} }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debounce = useRef(null);

  const load = useCallback(async (q) => {
    setLoading(true); setError('');
    try {
      const res = await deepLinksApi.list({ status_id: 1, query: q || '', limit: 100 });
      setRows(res?.data?.data || []);
    } catch (e) {
      setError('Could not load the library');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(query), 220);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [isOpen, query, load]);

  const choose = (entry) => { onClose(); onPick && onPick(entry); };

  return (
    <>
      {iconOnly ? (
        <Tooltip label="Pick from Deep Link Library" openDelay={300}>
          <IconButton aria-label="Pick from Deep Link Library" icon={<FiLink />} size="sm" variant="outline" onClick={onOpen} {...buttonProps} />
        </Tooltip>
      ) : (
        <Button leftIcon={<FiLink />} size="sm" variant="outline" onClick={onOpen} {...buttonProps}>{label}</Button>
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered scrollBehavior="inside">
        <ModalOverlay backdropFilter="blur(2px)" />
        <ModalContent borderRadius="16px">
          <ModalHeader fontSize="16px" fontWeight={700}>Deep Link Library</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={5}>
            <InputGroup mb={3}>
              <InputLeftElement pointerEvents="none"><FiSearch color="#9aa0a6" /></InputLeftElement>
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or category…" autoFocus />
            </InputGroup>

            {loading ? (
              <Center py={10}><Spinner /></Center>
            ) : error ? (
              <Center py={10}><Text color="red.400" fontSize="sm">{error}</Text></Center>
            ) : rows.length === 0 ? (
              <Center py={10}>
                <VStack color="gray.400" spacing={2}>
                  <FiInbox size={28} />
                  <Text fontSize="sm">{query ? 'No matching links' : 'No deep links yet — add some in Content → Deep Link Library'}</Text>
                </VStack>
              </Center>
            ) : (
              <VStack align="stretch" spacing={2}>
                {rows.map((r) => <LinkRow key={r.id} entry={r} onChoose={choose} />)}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
